import { c as _c } from "react/compiler-runtime";
/**
 * CmsPageBanner — shared hero banner for CMS pages.
 *
 * One component controls every CMS banner: image, breadcrumbs, subheading,
 * title, description, optional CTA, 10% side padding, and a consistent
 * 460px height. Change the look in ONE place, every CMS page updates.
 *
 * @example
 * <CmsPageBanner
 *   image="https://.../hero.jpg"
 *   breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'NDAA' }]}
 *   subheading="Understanding"
 *   title="Security Camera Compliance with the NDAA"
 *   description="Also covers the 2019 Hikvision / Dahua Sanctions…"
 *   cta={{ label: 'Shop Compliant Cameras', href: '#products' }}
 * />
 */

import Link from 'next/link';
export interface CmsPageBannerCrumb {
  label: string;
  href?: string;
}
export interface CmsPageBannerCta {
  label: string;
  href: string;
  external?: boolean;
}
export interface CmsPageBannerProps {
  image: string;
  imagePosition?: string;
  minHeight?: number;
  breadcrumbs: CmsPageBannerCrumb[];
  subheading?: string;
  title: string;
  description?: string | React.ReactNode;
  cta?: CmsPageBannerCta;
}
export function CmsPageBanner(t0) {
  const $ = _c(30);
  const {
    image,
    imagePosition: t1,
    minHeight: t2,
    breadcrumbs,
    subheading,
    title,
    description,
    cta
  } = t0;
  const imagePosition = t1 === undefined ? "center" : t1;
  const minHeight = t2 === undefined ? 460 : t2;
  const t3 = `linear-gradient(to top right, rgba(27, 80, 120, 0.6), rgba(235, 131, 34, 0.6)), url('${image}')`;
  let t4;
  if ($[0] !== imagePosition || $[1] !== minHeight || $[2] !== t3) {
    t4 = {
      minHeight,
      backgroundImage: t3,
      backgroundPosition: imagePosition,
      backgroundSize: "cover"
    };
    $[0] = imagePosition;
    $[1] = minHeight;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="absolute inset-0 bg-black/10" />;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== breadcrumbs) {
    let t7;
    if ($[7] !== breadcrumbs.length) {
      t7 = (crumb, index) => {
        const isLast = index === breadcrumbs.length - 1;
        return <li key={`${crumb.label}-${index}`} className="flex items-center gap-1">{index > 0 && <span className="text-white/70">›</span>}{crumb.href && !isLast ? <Link href={crumb.href} className="font-semibold text-[var(--scw-orange)] hover:underline">{crumb.label}</Link> : <span className="font-semibold text-white">{crumb.label}</span>}</li>;
      };
      $[7] = breadcrumbs.length;
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    t6 = breadcrumbs.map(t7);
    $[5] = breadcrumbs;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[9] !== t6) {
    t7 = <nav aria-label="Breadcrumb" className="pt-6 pb-6 text-sm"><ol className="flex flex-wrap items-center gap-1">{t6}</ol></nav>;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== subheading) {
    t8 = subheading && <span className="block text-sm md:text-base font-semibold text-white">{subheading}</span>;
    $[11] = subheading;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== title) {
    t9 = <h1 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-semibold text-white leading-tight">{title}</h1>;
    $[13] = title;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== description) {
    t10 = description && <div className="mt-4 text-base md:text-lg text-white/90">{typeof description === "string" ? <p>{description}</p> : description}</div>;
    $[15] = description;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== cta) {
    t11 = cta && <div className="mt-6">{cta.external ? <a href={cta.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-md px-8 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow" style={{
        backgroundColor: "var(--scw-orange)",
        border: "1px solid white"
      }}>{cta.label}</a> : <Link href={cta.href} className="inline-flex items-center justify-center rounded-md px-8 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow" style={{
        backgroundColor: "var(--scw-orange)",
        border: "1px solid white"
      }}>{cta.label}</Link>}</div>;
    $[17] = cta;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10 || $[20] !== t11 || $[21] !== t8 || $[22] !== t9) {
    t12 = <div className="max-w-3xl pb-10">{t8}{t9}{t10}{t11}</div>;
    $[19] = t10;
    $[20] = t11;
    $[21] = t8;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12 || $[25] !== t7) {
    t13 = <div className="relative w-full px-[10%]">{t7}{t12}</div>;
    $[24] = t12;
    $[25] = t7;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13 || $[28] !== t4) {
    t14 = <section className="relative flex items-center" style={t4}>{t5}{t13}</section>;
    $[27] = t13;
    $[28] = t4;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
}